/**
 * A STEP NOBODY CAN READ goes back to the agent, never to a person (founder ruling 2026-10-07:
 * "the stupidest question is I cant tell exactly what this step does").
 *
 * A call the side-effect classifier reads as 'unknown' is one nobody can judge: shown to a person
 * it is a card they cannot answer, and an unattended routine waits on it all night. The agent wrote
 * the step and can write it readably, so decideToolGate step 2c3 refuses it with what to change, at
 * every level and in every mode; no allow rule or grant turns it into an ask or a run. Every
 * producer of 'unknown', each with a readable form the copy names:
 *   - a remote workbench script or remote shell command (remote-code-reading.ts): no script at all;
 *     code that tries to reach an app, which the remote sandbox cannot (its own copy says so and names
 *     the way through); or code that runs a string, reaches a name at run time, spells a name outside
 *     ASCII, or reads the environment.
 *   - a COMPOSIO_MULTI_EXECUTE_TOOL item that is itself a batch, a workbench or the remote shell. (A
 *     list that cannot run at all never gets here: step 2c2 answers it first.)
 *   - Bash / PowerShell (git-command-catalog `gitCommandFamily`, its `unread` verdicts): a decoded payload
 *     run as code; a git or gh action beside code the analyzer cannot read (eval, a nested shell, a
 *     variable as the command); `git $SUB`, an alias defined inline, an unknown option before a push, push
 *     options it cannot read; `git config` of a key that redirects what git runs (alias, push, remote,
 *     url, credential, http, include) or `--edit`; `gh $X`, a gh alias or extension; a `gh api` request
 *     with an unknown flag, a body from a file, a variable method or write endpoint, or a GraphQL
 *     mutation. Most have a plainer spelling. The few that do not (a credential or http setting, an
 *     extension, a GraphQL mutation) get copy that says to stop and tell the person, so the agent never
 *     loops on them. A command that names no git or gh step (a decoded payload) gets its own plain copy.
 * PURE / TOTAL.
 */
import type { ToolCall } from '../contract/harness.js';
import { classifySideEffect, type SideEffectContext } from './side-effect-classifier.js';
import { COMPOSIO_REMOTE_SHELL_TOOL, isComposioExecutorMetaTool, isComposioRemoteCodeTool } from './composio-naming.js';
import { remoteCodeOf, remoteCodeReach } from './remote-code-reading.js';

/** The way through for remote code: the sandbox computes, the app's own tool reaches the app. */
const REMOTE_WAY_THROUGH =
  "The remote sandbox runs code and reads files, but it cannot call apps: call the app's tool, then read the saved result here.";

/** Remote code that tries to reach an app (remote-code-reading.ts 'apps'): it could not run as written. */
export const REMOTE_CODE_CANNOT_CALL_APPS_MESSAGE =
  "This did not run: the remote sandbox runs code and reads files, but it cannot call apps. Call the app's tool, or " +
  'several in one COMPOSIO_MULTI_EXECUTE_TOOL call, then read the saved result here. Keep run_composio_tool, ' +
  'proxy_execute and Composio itself (its SDK, API address, keys, or its name in text) out of the code; reading ' +
  'the files it saved is fine.';

export const UNREADABLE_SCRIPT_MESSAGE =
  "I can't check this script, so it did not run. Write the Python plainly, in ASCII outside strings and comments: " +
  'no exec, eval, getattr or get_ipython, no lookups through globals(), vars() or __dict__, and no reading the ' +
  `environment. ${REMOTE_WAY_THROUGH}`;

export const UNREADABLE_REMOTE_COMMAND_MESSAGE =
  "I can't check this command, so it did not run. Write it plainly, in ASCII outside quotes and comments: no code " +
  `built or run from strings, and no reading the environment. ${REMOTE_WAY_THROUGH}`;

/**
 * Remote code that would otherwise wait for a person (an ask rule, a credit approval, anything): it goes
 * back to the agent instead (decideToolGate), so remote code only ever allows or denies.
 */
export const REMOTE_CODE_CANNOT_ASK_MESSAGE =
  "This needs your owner's yes, and code in the remote sandbox cannot ask them. Do it in this workspace instead, " +
  'where they can approve it.';

export const UNREADABLE_BATCH_MESSAGE =
  "I can't check this batch, so it did not run: one item is itself a batch, a workbench script or a remote " +
  'shell command. Call those on their own, and keep only plain app tools in the batch.';

/** A Bash command that runs code it decodes or builds while running, with no git or gh step in sight. */
export const UNREADABLE_COMMAND_MESSAGE =
  "I can't check this command, so it did not run. It runs code it decodes or builds while running. Write " +
  'the commands out plainly, each one as itself, and run them directly.';

export const UNREADABLE_GIT_COMMAND_MESSAGE =
  "I can't check this command, so it did not run. Write each git or gh step out in full, as its own command: no " +
  'decoded or generated code, no eval or variable in place of a command, method or endpoint, and no alias, ' +
  'extension, config change or GraphQL mutation that changes what git or gh does. If there is no plainer way, ' +
  'stop and tell the person what you need to run and why.';

const NO_SCRIPT_MESSAGE = 'Nothing ran: the script was empty. Put the Python in code_to_execute and send it again.';
const NO_COMMAND_MESSAGE = 'Nothing ran: the command was empty. Put it in command and send it again.';
const UNREADABLE_STEP_MESSAGE = "I can't check this step, so it did not run. Name the tool and its arguments plainly and send it again.";
/** A git or gh word anywhere in the command: the git copy names what to write out. */
const NAMES_GIT = /(?:^|[^\w-])(?:git|gh)(?![\w-])/;

/** The refusal the agent reads when nobody can read `call`, else null. */
export function unreadableStepDenial(call: ToolCall, ctx: SideEffectContext): string | null {
  if (classifySideEffect(call, ctx) !== 'unknown') return null;
  const tool = call.toolName;
  if (tool === 'Bash' || tool === 'PowerShell') {
    const command = (call.toolInput as { command?: unknown } | null)?.command;
    return typeof command === 'string' && NAMES_GIT.test(command) ? UNREADABLE_GIT_COMMAND_MESSAGE : UNREADABLE_COMMAND_MESSAGE;
  }
  if (isComposioRemoteCodeTool(tool)) {
    const shell = tool === COMPOSIO_REMOTE_SHELL_TOOL;
    const code = remoteCodeOf(call);
    if (code === null) return shell ? NO_COMMAND_MESSAGE : NO_SCRIPT_MESSAGE;
    if (remoteCodeReach(code) === 'apps') return REMOTE_CODE_CANNOT_CALL_APPS_MESSAGE;
    return shell ? UNREADABLE_REMOTE_COMMAND_MESSAGE : UNREADABLE_SCRIPT_MESSAGE;
  }
  if (isComposioExecutorMetaTool(tool)) return UNREADABLE_BATCH_MESSAGE;
  return UNREADABLE_STEP_MESSAGE;
}
